import { AfterViewInit, Component, Input, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';

import { BaseUI } from '../../base.ui';
import { ParentEvents } from '../../../utils/parent-events.service';

export interface ChartData {
    name: string;
    weight: number;
    value: number;
}

@Component({
    selector: 'thr-chart-doughnut',
    templateUrl: './doughnut-chart.component.html',
    styleUrls: ['./doughnut-chart.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class DoughnutChartComponent extends BaseUI implements AfterViewInit, OnChanges {
    readonly COLORS = ['#0077b6', '#db4c40', '#2a9d8f', '#f48c06', '#fee440', '#f4acb7', '#9d8189', '#70d6ff'];
    readonly CX = 80;
    readonly CY = 80;
    readonly RADIUS = 60;
    readonly STROKE_WIDTH = 30;

    drawData = [];
    total = 0;

    @Input()
    data: ChartData[];

    constructor(parentEvents: ParentEvents) {
        super(parentEvents);
    }

    get adjustedCircumference(): number {
        return this.circumference - 2;
    }

    get circumference(): number {
        return 2 * Math.PI * this.RADIUS;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ('data' in changes || ('legend' in changes && !!this.data)) {
            this.sortData();
            this.calculateTotal();
            this.calculateDrawData();
        }
    }

    private sortData(): void {
        this.data.sort((a, b) => b.value - a.value);
    }

    private calculateTotal(): void {
        this.total = 0;
        this.data.forEach((d) => (this.total += d.value));
    }

    private calculateDrawData(): void {
        // start at -90deg so that the largest segment is perpendicular to top
        let angleOffset = -90;

        this.drawData = [];
        this.data.forEach((d) => {
            const { x, y } = this.calculateTextCoords(d.value, angleOffset);
            this.drawData.push({
                degrees: angleOffset,
                textX: x,
                textY: y,
            });
            angleOffset = this.percentage(d.value) * 360 + angleOffset;
        });
    }

    private calculateTextCoords(dataVal, angleOffset): { x: number; y: number } {
        // t must be radians
        // x(t) = r cos(t) + j
        // y(t) = r sin(t) + j
        const angle = (this.percentage(dataVal) * 360) / 2 + angleOffset;
        const radians = this.degreesToRadians(angle);

        return {
            x: this.RADIUS * Math.cos(radians) + this.CX,
            y: this.RADIUS * Math.sin(radians) + this.CY,
        };
    }

    private percentage(value): number {
        return value / this.total;
    }

    private degreesToRadians(angle): number {
        return angle * (Math.PI / 180);
    }

    calculateStrokeDashOffset(value, circumference): number {
        const strokeDiff = this.percentage(value) * circumference;
        return circumference - strokeDiff;
    }

    percentageString(value): string {
        return `${Math.round(this.percentage(value) * 100)}%`;
    }

    circleTransformValue(index): string {
        return `rotate(${this.drawData[index].degrees}, ${this.CX}, ${this.CY})`;
    }

    segmentBigEnough(value): boolean {
        return Math.round(this.percentage(value) * 100) > 5;
    }
}
